import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login, demoLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const doLogin = async (em, pw) => {
    await login(em, pw);
    toast.success("Signed in");
    navigate("/");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await doLogin(email, password);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const viewDemo = async () => {
    setDemoLoading(true);
    try {
      await demoLogin();
      toast.success("Signed in as Demo Client · Demo Brand");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Demo unavailable");
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* LEFT — dark branded panel */}
      <div
        className="relative hidden lg:flex flex-col justify-between p-14 text-white overflow-hidden"
        style={{ background: "#050B1A" }}
        data-testid="login-brand-panel"
      >
        {/* Subtle grid + glow */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div
          className="pointer-events-none absolute -top-24 -left-24 h-[540px] w-[540px] rounded-full blur-3xl"
          style={{ background: "radial-gradient(closest-side, rgba(34,211,238,0.20), transparent 70%)" }}
        />
        <div
          className="pointer-events-none absolute -bottom-32 right-[-140px] h-[520px] w-[520px] rounded-full blur-3xl"
          style={{ background: "radial-gradient(closest-side, rgba(6,182,212,0.14), transparent 70%)" }}
        />

        <div className="relative z-10">
          <div className="flex items-baseline gap-3">
            <div className="font-display text-5xl font-bold tracking-tight" data-testid="brand-wordmark">MarginOps</div>
            <div className="h-2 w-2 rounded-full bg-cyan-400" />
          </div>
          <div className="mt-2 text-[11px] tracking-[0.28em] uppercase text-cyan-300/80 font-mono">
            by Ask Strategic Partners
          </div>
        </div>

        {/* mid — 3 pillars */}
        <div className="relative z-10 space-y-8 max-w-md">
          <div className="text-white/80 text-sm uppercase tracking-[0.24em]">Built for D2C operators</div>
          <div className="grid grid-cols-3 gap-3">
            {[
              { k: "Currency", v: "₹ INR" },
              { k: "Cadence",  v: "Daily" },
              { k: "Metric",   v: "Real Net" },
            ].map((p) => (
              <div key={p.k} className="border border-white/10 bg-white/[0.03] p-3">
                <div className="text-[10px] uppercase tracking-widest text-white/50">{p.k}</div>
                <div className="mt-1 font-mono text-base">{p.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <div className="font-display text-2xl font-semibold leading-snug max-w-lg">
            Unveil the true profit<br />in every transaction.
          </div>
          <div className="mt-4 text-[11px] tracking-[0.24em] uppercase text-white/40 font-mono">
            Confidential · Financial Operations Suite
          </div>
        </div>
      </div>

      {/* RIGHT — clean white card */}
      <div className="flex items-center justify-center p-8 bg-white text-neutral-900" data-testid="login-panel">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="text-[11px] font-mono tracking-[0.32em] text-cyan-600 uppercase" data-testid="marginops-logo">
              MARGINOPS
            </div>
            <h1 className="mt-3 font-display text-4xl font-bold tracking-tight text-neutral-900">
              Welcome Back
            </h1>
            <p className="mt-2 text-sm text-neutral-500">Your Unit Economics, Clarified.</p>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label className="text-[11px] tracking-widest uppercase text-neutral-500">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-none mt-2 h-11 border-neutral-300 focus-visible:ring-cyan-500 focus-visible:border-cyan-500"
              />
            </div>
            <div>
              <Label className="text-[11px] tracking-widest uppercase text-neutral-500">Password</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-none mt-2 h-11 border-neutral-300 focus-visible:ring-cyan-500 focus-visible:border-cyan-500"
              />
            </div>

            <Button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-none bg-cyan-500 hover:bg-cyan-600 text-white font-semibold tracking-wide shadow-[0_10px_30px_-12px_rgba(6,182,212,0.6)] transition-all"
            >
              {loading ? "Signing in…" : (<span className="inline-flex items-center gap-2">Sign In <ArrowRight className="h-4 w-4" /></span>)}
            </Button>

            <div className="text-xs text-neutral-500">
              New here?{" "}
              <Link to="/register" className="underline underline-offset-2 text-neutral-900" data-testid="link-register">
                Create an account
              </Link>
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-neutral-200">
            <Button
              type="button"
              onClick={viewDemo}
              disabled={demoLoading}
              variant="outline"
              className="w-full h-11 rounded-none border-neutral-300 hover:border-cyan-500 hover:text-cyan-600 text-neutral-700 tracking-wide"
              data-testid="view-demo-btn"
            >
              {demoLoading ? "Loading demo…" : "View Demo Dashboard"}
            </Button>
            <div className="mt-3 text-[10px] font-mono text-neutral-400 text-center">
              Read-only sample data · Demo Brand workspace
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
